import { downloadFileFromResponse } from "apis/localhost";
import {
  OUTLINE_SECTION_STATUS,
  OUTLINE_SECTION_STATUS_LABELS,
} from "constants/proposals";
import ExcelJS from "exceljs";
import { DATE_VARIANTS, formatDate } from "utils/formatDate";
import { getFullName } from "utils/getFullName";

export const OUTLINE_EXPORT_HEADERS = [
  "Section Name",
  "Level",
  "Trace",
  "Pg. Count",
  "Status",
  "Assignee",
  "Doc Last Updated",
  "Notes",
];

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const SHEET_NAME = "Outline";

const NO_DATE = "-";

const COLUMN_WIDTHS = [56, 8, 8, 18, 14, 26, 20, 48];

const MAX_FILE_STEM_LENGTH = 120;

const toText = (value) => (value == null ? "" : String(value));

// getFullName destructures its argument, which defaults only on undefined — a null entry would
// throw and abort the whole export.
const toAssigneeNames = (assignees) =>
  (assignees || []).map(getFullName).filter(Boolean).join(", ");

const toStatusLabel = (status) =>
  OUTLINE_SECTION_STATUS_LABELS[status] ??
  OUTLINE_SECTION_STATUS_LABELS[OUTLINE_SECTION_STATUS.NOT_STARTED];

const toTraceCount = (coveredPointIds) => (coveredPointIds || []).length || "";

// Collapsed parents and on-screen filters live in table state rather than in this list, so the
// export is always the complete outline. Hierarchy is carried as a real cell indent rather than
// padded spaces, which would corrupt sorting and filtering in Excel.
export const buildOutlineRows = (sections = []) =>
  (sections || []).map((section) => ({
    indent: Math.max(0, (section.level ?? 1) - 1),
    values: [
      toText(section.title),
      `L${section.level ?? 1}`,
      toTraceCount(section.coveredPointIds),
      toText(section.pageLimit),
      toStatusLabel(section.status),
      toAssigneeNames(section.assignees),
      formatDate(section.docLastUpdated, DATE_VARIANTS.DOT_TIME) || NO_DATE,
      toText(section.complianceNotes),
    ],
  }));

// Stripping accents first keeps "Éclair Défense" readable instead of mangling it to "clair-d-fense".
// Matching a single non-alphanumeric run then trimming one leading and one trailing hyphen keeps
// the pattern free of the backtracking that makes an unanchored "-+$" quadratic (Sonar S5852).
const toFileStem = (ownerName) => {
  const slug = (ownerName || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, MAX_FILE_STEM_LENGTH)
    .replace(/-$/, "");
  return slug ? `${slug}-outline` : "outline";
};

export const downloadOutlineExcel = async (sections, ownerName) => {
  const rows = buildOutlineRows(sections);

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(SHEET_NAME);

  sheet.columns = COLUMN_WIDTHS.map((width) => ({ width }));
  sheet.views = [{ state: "frozen", ySplit: 1 }];

  const headerRow = sheet.addRow(OUTLINE_EXPORT_HEADERS);
  headerRow.font = { bold: true };

  // The filter range must span the data rows, not just the header, or the dropdowns list nothing.
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: rows.length + 1, column: OUTLINE_EXPORT_HEADERS.length },
  };

  rows.forEach(({ values, indent }) => {
    const row = sheet.addRow(values);
    row.getCell(1).alignment = { indent, vertical: "top", wrapText: true };
  });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadFileFromResponse(buffer, `${toFileStem(ownerName)}.xlsx`, {
    type: XLSX_MIME,
  });
};
